import React from "react";

/**
 * CommandCenter — multi-pane "all apps at a glance" view.
 * Triggered by command_center voice action. Lays out one pane per app showing
 * revenue, ad spend, content views, unread messages, open issues + status toggles.
 */
export default function CommandCenter({ data }) {
  if (!data?.panes?.length) return null;

  const fmtMoney = (n) => `£${Math.round(n || 0).toLocaleString()}`;
  const fmtNum   = (n) => (n || 0).toLocaleString();

  return (
    <div data-testid="command-center">
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 max-h-[78vh] overflow-y-auto pr-1">
        {data.panes.map((p, i) => (
          <Pane key={p.app_key || i} p={p} fmtMoney={fmtMoney} fmtNum={fmtNum} />
        ))}
      </div>
    </div>
  );
}

function Pane({ p, fmtMoney, fmtNum }) {
  const isTop = p.performance_rank === 1;
  const hasIssues = (p.open_issues || 0) > 0;
  const hasUnread = (p.unread_messages || 0) > 0;
  const roiOk = (p.revenue_7d || 0) >= (p.ad_spend_7d || 0) * 3; // simple traffic-light heuristic

  return (
    <div
      className={`rc-panel relative p-4 ${isTop ? "border-[var(--rc-gold)]" : ""}`}
      data-testid={`cc-pane-${p.app_key}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="rc-label text-[9px] opacity-70">{(p.category || "").toUpperCase()}</div>
          <div className="font-display text-base rc-glow-text tracking-wide mt-0.5">{p.name}</div>
        </div>
        {p.performance_rank && (
          <div className={`text-[9px] rc-label border ${isTop ? "border-[var(--rc-gold)] rc-glow-text" : "border-[var(--rc-gold-dim)]"} px-1.5 py-0.5`}>
            RANK #{p.performance_rank}
          </div>
        )}
      </div>

      {/* Top KPI row */}
      <div className="grid grid-cols-3 gap-2 mb-3">
        <Stat label="REV 7D" value={fmtMoney(p.revenue_7d)} accent />
        <Stat label="SPEND 7D" value={fmtMoney(p.ad_spend_7d)} tone={roiOk ? "ok" : "warn"} />
        <Stat label="VIEWS 7D" value={fmtNum(p.content_views_7d)} />
      </div>

      {/* Alerts row */}
      <div className="grid grid-cols-2 gap-2 mb-3">
        <AlertBox label="UNREAD" count={p.unread_messages} tone={hasUnread ? "warn" : "muted"} />
        <AlertBox label="ISSUES" count={p.open_issues} tone={hasIssues ? "danger" : "muted"} />
      </div>

      {/* Toggles */}
      {p.toggles && Object.keys(p.toggles).length > 0 && (
        <div className="border-t border-[var(--rc-gold-dim)] pt-2 mt-2">
          <div className="rc-label text-[9px] mb-1.5 opacity-70">STATE</div>
          <div className="flex flex-wrap gap-1.5">
            {Object.entries(p.toggles).map(([k, v]) => (
              <div
                key={k}
                className={`text-[9px] tracking-wide px-1.5 py-0.5 border ${v ? "border-[var(--rc-gold)] rc-glow-text" : "border-[var(--rc-gold-dim)] opacity-50"}`}
                data-testid={`cc-toggle-${p.app_key}-${k}`}
              >
                {k.toUpperCase().replace(/_/g, " ")} · {v ? "ON" : "OFF"}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, accent, tone }) {
  const color = tone === "warn" ? "text-amber-400"
              : tone === "ok"   ? "rc-glow-text"
              : accent          ? "rc-glow-text"
              : "text-[var(--rc-text)]";
  return (
    <div className="border border-[var(--rc-gold-dim)] p-2">
      <div className="rc-label text-[8px] opacity-70">{label}</div>
      <div className={`font-display text-sm mt-0.5 tabular-nums ${color}`}>{value}</div>
    </div>
  );
}

function AlertBox({ label, count, tone }) {
  const cn = tone === "danger" ? "border-red-500/60 text-red-400"
            : tone === "warn"  ? "border-amber-500/60 text-amber-400"
            : "border-[var(--rc-gold-dim)] text-[var(--rc-text-dim)]";
  return (
    <div className={`border ${cn} p-2 flex items-center justify-between`}>
      <div className="rc-label text-[8px] opacity-80">{label}</div>
      <div className="font-display text-base tabular-nums">{count || 0}</div>
    </div>
  );
}
